import type { QueryClient, QueryKey } from "@tanstack/react-query"
import { api } from "@/lib/api"

/**
 * Die EINE Definition, wie eine Mail geladen wird.
 *
 * 🔴 Anlass: „Fehler: Missing queryFn: '[\"email\",\"…\"]'" stand jetzt zum
 * dritten Mal rot in der Inbox (07.08., 02.09., 04.09.2026) — jedes Mal in
 * einer anderen Ausloesung, jedes Mal mit derselben Wurzel.
 *
 * In React Query gehoert die `queryFn` nicht dem Observer, sondern dem
 * **Query**: wer zuletzt seine Optionen setzt, gewinnt. Am Schluessel
 * `["email", id]` haengen drei Komponenten, die die Mail wirklich holen —
 * `EmailsList` (Detailspalte), `EmailDetail` (eigene Seite) und `EmailModal`
 * (aus dem Benachrichtigungs-Banner) — und frueher zusaetzlich ein Mitleser.
 * Solange jede von ihnen die Abfrage selbst mitdefiniert, kann jede sie den
 * anderen auch wegnehmen. Zwei Anlaeufe haben genau das getan (Dummy-`queryFn`
 * schrieb `{}` ueber die Daten, `skipToken` nahm die Funktion weg), ein dritter
 * Weg ist offenbar geblieben.
 *
 * 🔑 Deshalb definiert hier **niemand am Observer mehr etwas**. Die Abfrage
 * gehoert dem QueryClient: `emailQueryDefaults(qc)` haengt sie einmal an das
 * Praefix `["email"]`. Damit hat jeder Query unter diesem Schluessel eine
 * `queryFn` — unabhaengig davon, welche Komponente zuletzt gebunden hat, ob
 * eine gerade unmountet oder ob jemand spaeter einen vierten Leser dazustellt.
 * „Missing queryFn" ist fuer diesen Schluessel damit nicht mehr moeglich.
 *
 * Verwandt: `lib/cacheMitleser.ts` — wer nur mitlesen will, meldet gar keinen
 * Observer an.
 */

/** Die ID steckt an zweiter Stelle im Schluessel: `["email", "4711"]`. */
function idAusSchluessel(key: QueryKey): number | null {
  const roh = Array.isArray(key) ? key[1] : null
  const n = Number(roh)
  return Number.isFinite(n) && n > 0 ? n : null
}

export async function ladeEmail(emailId: number) {
  const { data, error } = await api.GET("/api/v1/emails/{email_id}", {
    params: { path: { email_id: emailId } },
  })
  if (error) throw new Error(String(error))
  // 🔴 `data` kann ohne `error` undefined sein — etwa wenn der Refetch
  // abgebrochen wird, was direkt nach `invalidateQueries` passieren kann.
  // React Query verbietet undefined als Ergebnis und wirft dann selbst:
  // "Query data cannot be undefined ... [\"email\",\"59113\"]". Das landete
  // wortwoertlich als roter Text in der Detailspalte, nachdem der Nutzer
  // Aktionen ausgefuehrt hatte — obwohl serverseitig alles geklappt hat
  // (07.08.2026, Mail #59113: Kontakt, Projekt und Aufgabe waren korrekt
  // angelegt). Ein eigener Fehler ist lesbar und wird wiederholt.
  if (!data) throw new Error("E-Mail konnte nicht geladen werden")
  return data
}

/**
 * Der Typ der geladenen Mail. Er kam frueher aus der `queryFn` der jeweiligen
 * Komponente — jetzt, wo die Abfrage dem Client gehoert, kommt er von hier.
 * Die Leser geben ihn an `useQuery<EmailAntwort>` weiter.
 */
export type EmailAntwort = Awaited<ReturnType<typeof ladeEmail>>

/**
 * Haengt die Abfrage an das Schluessel-Praefix `["email"]`.
 *
 * 🔑 Nur `["email", …]` — `["emails"]`, `["email-thread", …]` und
 * `["hero-options", "email", …]` sind andere Schluessel und bleiben unberuehrt
 * (React Query vergleicht die Elemente einzeln, nicht als Text).
 */
export function emailQueryDefaults(qc: QueryClient) {
  qc.setQueryDefaults(["email"], {
    retry: 2,
    queryFn: ({ queryKey }) => {
      const id = idAusSchluessel(queryKey)
      // Ohne brauchbare ID gar nicht erst laden. Das ist kein Fehlerfall:
      // die Leser sind ueber `enabled` abgesichert, das hier ist das Netz
      // darunter.
      if (id === null) throw new Error("E-Mail konnte nicht geladen werden")
      return ladeEmail(id)
    },
  })
}
